import { Loading } from '@perch-labs/shrike';
import camelCase from 'lodash/camelCase';
import { StyledModal } from '~/components/dialogs/dialog.style';
import { Link, LinkProps } from '~/components/link';
import { ScrollablePdf } from '~/components/terms/scrollable-terms/scrollable-pdf.component';
import { usePersonalizedContract, useToggle } from '~/hooks';
import { ContractVersion } from '~/typings/context';
import { IS_PERCH_ENERGY, TERMS_URL } from '~/utils/constants';
import { determineLanguage } from '~/utils/funnels';
import { ContractLinkScreen, EventName, Logger } from '~/utils/logger';

// Personalized contract link that shows modal and fetches personalized contract
export const PersonalizedContractLink = ({
  contract,
  utilityAccountId,
  planId,
  linkProps,
  screen,
}: {
  contract: ContractVersion;
  utilityAccountId?: number;
  planId?: number;
  linkProps?: LinkProps;
  screen: ContractLinkScreen;
}) => {
  const [isOpen, toggle] = useToggle(false);
  const language = determineLanguage() || 'en';

  const { personalizedFile, error, isLoading } = usePersonalizedContract({
    contractId: contract.id,
    utilityAccountId,
    planId,
  });
  const isLegalScreen = screen === EventName.LegalScreen;
  const href = `${TERMS_URL}?id=${contract.id}&lang=${language}&entity=${
    IS_PERCH_ENERGY ? 'perch' : 'arcadia'
  }`;

  const handleOnClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    if (IS_PERCH_ENERGY) {
      // Status-less append: opening a preview is an attribute of the screen, not a
      // lifecycle transition, so it must never re-render a completed screen.
      // (SUBEXP-3193)
      Logger.logEvent(
        isLegalScreen
          ? { contractPreviewOpened: true }
          : { [`${camelCase(contract.displayName)}Clicked`]: true },
        screen,
      );
    }

    if (personalizedFile) {
      toggle(true);
      if (isLegalScreen && IS_PERCH_ENERGY) {
        Logger.logEvent({ personalizedContractReceived: true }, EventName.LegalScreen);
      }
    } else if (error) {
      window.open(href, '_blank');
    } else if (isLoading) {
      toggle(true);
    }
  };

  return (
    <>
      {!!isOpen && (
        <StyledModal
          isOpen={isOpen}
          onDismiss={() => {
            toggle(false);
          }}
          size="large"
          aria-label="personalized-contract-preview"
          data-testid="personalized-contract-preview"
        >
          <StyledModal.HeaderCentered
            title={contract.displayName}
            onClose={() => {
              toggle(false);
            }}
          />
          {isLoading || !personalizedFile ? (
            <div style={{ padding: '2rem 0' }}>
              <Loading />
            </div>
          ) : (
            <ScrollablePdf pdfUrl={personalizedFile || ''} />
          )}
        </StyledModal>
      )}
      <Link
        color="primary"
        id={`ContractVersion-${contract.id}`}
        onClick={handleOnClick}
        href={href}
        {...linkProps}
        openInNewTab={false}
      >
        {contract.displayName}
      </Link>
    </>
  );
};
